/**
 * Org Settings → Audit log (#1119 Slice 3).
 *
 * Read-only "who did what, when" view of the org's own activity. Rows
 * are server-redacted (the API only ever returns safe, derived fields —
 * never raw before/after/meta), so this component just renders what it
 * receives. Layout mirrors the admin Audit table; see
 * `docs/mockups/org-audit-log-page.html`.
 *
 * Gated by `audit.read` (owner/admin) — the server enforces it on
 * `GET /api/orgs/:orgId/audit` independently. Filters/facets land in
 * Slice 4; this slice ships the table + pagination + states.
 */
import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import { AuditCategoryBadge } from '@/components/AuditCategoryBadge'
import { categoryMetaByKey } from '@/lib/auditCategories'
import { orgsAPI, type OrgAuditRow, type OrgWithRole } from '@/lib/api'
import { useCanI } from '@/hooks/useCanI'
import { formatDateTime } from '@/lib/utils'

const PAGE_SIZE = 50

/** Customer-visible category facets (matches the server's category keys). */
const CATEGORY_FACETS = ['members', 'workspaces', 'teams', 'organization', 'test-data', 'integrations'] as const
const RANGES = [
  { key: 'all', label: 'All time', days: 0 },
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
] as const
type RangeKey = (typeof RANGES)[number]['key']

function chipClass(active: boolean): string {
  return `rounded-md px-2.5 py-1 text-xs ${
    active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
  }`
}

export function OrgAudit({ org }: { org: OrgWithRole }) {
  const canRead = useCanI('audit.read', org._id)
  // Audit log is a forensic surface — render timestamps in UTC so
  // investigators reading rows across timezones (support tickets,
  // cross-team coordination) see one unambiguous stamp. The admin
  // Audit page (the other surface this consistency comment used to
  // reference) also switched to `'absoluteUTC'` in #1218 Group E.
  const [rows, setRows] = useState<OrgAuditRow[]>([])
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Filters (#1119 Slice 4). All filtering is server-side so it works
  // across pages; changing any filter resets the offset to the first page.
  const [category, setCategory] = useState('') // '' = all categories
  const [range, setRange] = useState<RangeKey>('all')
  const [actor, setActor] = useState('') // applied actor query (email)
  const [actorInput, setActorInput] = useState('') // the text field
  const filtersActive = !!(category || range !== 'all' || actor)

  /** Set a filter and jump back to the first page. */
  function applyFilter(fn: () => void) {
    fn()
    setOffset(0)
  }

  useEffect(() => {
    if (!canRead) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    // Compute the window INSIDE the effect, keyed on `range` — a render-time
    // Date.now() would change every render and, in the dep list, thrash the
    // fetch (Copilot #1131). Keyed on `range`, it's stable until the user
    // changes the range.
    const days = RANGES.find((r) => r.key === range)!.days
    const since = days === 0 ? undefined : new Date(Date.now() - days * 86_400_000).toISOString()
    orgsAPI
      .getAuditLog(org._id, {
        limit: PAGE_SIZE,
        offset,
        category: category || undefined,
        since,
        actor: actor || undefined,
      })
      .then((page) => {
        if (cancelled) return
        setRows(page.rows)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to read the audit log')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [org._id, offset, canRead, category, range, actor])

  if (!canRead) {
    return (
      <p className="text-sm text-muted-foreground">
        Only owners and admins can view the audit log.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Audit log</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          A record of who did what in your organization. Read-only.
        </p>
      </div>

      {/* Filters (#1119 Slice 4) — all server-side, so they work across
          pages; changing one resets to the first page via applyFilter. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => applyFilter(() => setCategory(''))} className={chipClass(category === '')}>
            All
          </button>
          {CATEGORY_FACETS.map((k) => (
            <button key={k} onClick={() => applyFilter(() => setCategory(k))} className={chipClass(category === k)}>
              {categoryMetaByKey(k).label}
            </button>
          ))}
        </div>
        <select
          value={range}
          onChange={(e) => applyFilter(() => setRange(e.target.value as RangeKey))}
          className="h-8 rounded-md border border-border bg-background px-2 text-xs"
          aria-label="Time range"
        >
          {RANGES.map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
        </select>
        <Input
          value={actorInput}
          onChange={(e) => setActorInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applyFilter(() => setActor(actorInput.trim()))
          }}
          placeholder="Filter by email…"
          className="h-8 w-44 text-xs"
          aria-label="Filter by actor email"
          clearable
        />
        {filtersActive && (
          <button
            onClick={() =>
              applyFilter(() => {
                setCategory('')
                setRange('all')
                setActor('')
                setActorInput('')
              })
            }
            className="text-xs text-primary hover:underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* Error is a non-destructive banner ABOVE the table — a failed page
          load must not strip the table + pager, or the user is stranded
          with no way back to a working offset (Copilot #1130). */}
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {loading && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading activity…</p>
      ) : rows.length === 0 && offset === 0 && !error ? (
        <p className="text-sm text-muted-foreground">
          {filtersActive
            ? 'No activity matches these filters.'
            : 'No activity yet. Actions across your organization will appear here.'}
        </p>
      ) : rows.length > 0 || offset > 0 ? (
        <>
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">When</TableHead>
                  <TableHead className="w-[140px]">Who</TableHead>
                  <TableHead className="w-[110px]">Category</TableHead>
                  <TableHead>What happened</TableHead>
                  <TableHead className="w-[70px]">Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap align-top">
                      {formatDateTime(r.ts, 'absoluteUTC')}
                    </TableCell>
                    <TableCell className="text-sm align-top">{r.actor}</TableCell>
                    <TableCell className="align-top">
                      <AuditCategoryBadge categoryKey={r.category} />
                    </TableCell>
                    <TableCell className="text-sm align-top">{r.description}</TableCell>
                    <TableCell className="align-top">
                      <Badge variant={r.result === 'success' ? 'success' : 'destructive'}>
                        {r.result === 'success' ? 'OK' : 'Fail'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {offset === 0 ? 'Most recent activity' : `From #${offset + 1}`}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              >
                ← Newer
              </Button>
              <Button
                variant="outline"
                size="sm"
                // A short page means there's nothing older to fetch.
                disabled={rows.length < PAGE_SIZE}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
              >
                Older →
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
